// @flow
import cloneDeep from 'lodash/cloneDeep';
import * as React from 'react';
import { ErrorMessage, FieldArray } from 'formik';
import { defineMessages, FormattedMessage } from 'react-intl';
import { withTheme } from 'styled-components';
import type { SchemaType } from 'speed-js-react/src/components/forms/types';
import SchemaForm from 'speed-js-react/src/components/forms/SchemaForm';
import { Button, Box, Text } from 'grommet';
import { FormAdd, FormSubtract } from 'grommet-icons';
import * as Yup from 'yup';
import { memoize, isEmpty, isEqual } from 'lodash';
import memoizeOne from 'memoize-one';
import { triggerNativeEvent } from 'speed-js-react-web/src/utils/events';
import RequiredAsterisk from '../RequiredAsterisk';

type Props = {
    field: {
        onChange: (e: SyntheticInputEvent<number>) => void,
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        value: ?Array<Object>;
        name: string;
    },
    form: {
        setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
    },
    schema: SchemaType,
    rowSpecificSchemas?: Object,
    disablePlusButton?: boolean,
    disableFormButton?: boolean,
    disableFormClose?: boolean,
    required: boolean,
    label: string,
    help_text: string,
    theme: Object,
    many: boolean,
}

const messages = defineMessages({
    required: {
        id: 'inlineform-required',
        defaultMessage: 'This field is required.',
    },
});

// eslint-disable-next-line
class InlineForm extends React.Component<Props> {
    parseSchema: typeof SchemaForm.parseSchema;

    static defaultProps = {
        rowSpecificSchemas: undefined,
        disablePlusButton: false,
        disableFormButton: false,
        disableFormClose: false,
    };

    static getValidationSchema(field: { validation: Object, value: any, ui: { schema: SchemaType } }) {
        let errMsg;
        if (field.validation.many) {
            errMsg = Yup.array();
        } else {
            errMsg = Yup.object();
        }

        if (field.validation.required) {
            errMsg = errMsg.required(messages.required);
        }
        return errMsg.nullable();
    }

    constructor(props: Props) {
        super(props);

        this.parseSchema = memoizeOne(SchemaForm.parseSchema);
        this.schemaValues = memoize(this.schemaValues);
        this.schemaEntries = memoize(this.schemaEntries);
        this.handleAddRow = memoize(this.handleAddRow);
        this.state = {
            innerRowSpecificSchemas: props.rowSpecificSchemas,
        };
    }

    componentDidUpdate(prevProps: Props) {
        if (!isEqual(prevProps.rowSpecificSchemas, this.props.rowSpecificSchemas)) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                innerRowSpecificSchemas: this.props.rowSpecificSchemas,
            });
        }
    }

    schemaValues = (schema: Object) => Object.values(schema[0]);

    schemaEntries = (schema: Object) => Object.entries(schema[0]);

    handleAddRow = (arrayHelpers: Object) => (
        (e: SyntheticMouseEvent<HTMLButtonElement>) => {
            e.preventDefault();

            const {
                field: { value, name },
                form: { setFieldTouched }, schema,
            } = this.props;
            const newIndex = (value || []).length;

            const { fields } = schema;
            const row = {};
            fields.forEach((schemaField) => {
                const [fieldName, fieldValue] = schemaField;
                if (!fieldValue.read_only && fieldValue.validation.required) {
                    row[fieldName] = '';
                }
            });

            arrayHelpers.push(row);

            // Set the touched flag to all fields in the new row so that validation errors wil be shown
            fields.forEach((schemaField) => {
                const [fieldName, fieldValue] = schemaField;
                setFieldTouched(`${name}.${newIndex}.${fieldName}`, true, false);
            });

            triggerNativeEvent(e.target, 'change');
        }
    );

    renderRow = (arrayHelpers: Object) => {
        const {
            field: { value, name }, schema, label, disablePlusButton, disableFormButton, disableFormClose,
        } = this.props;
        const { innerRowSpecificSchemas } = this.state;
        const values = value || [];
        const handleAddRow = this.handleAddRow(arrayHelpers);
        const addItemLabel = (
            <>
                <FormattedMessage
                    id="add-inline-form-label"
                    defaultMessage="Add"
                />
                {
                    ` ${String(label).toLowerCase()}`
                }
            </>
        );

        return (
            <Box>
                {!disableFormButton && values && values.length > 0 ? (
                    values.map((fieldValues, index) => (
                        <Box
                            // eslint-disable-next-line
                            key={index}
                            direction="column"
                            gap="small"
                            margin="xsmall"
                            align="center"
                            border="bottom"
                            pad={{ bottom: 'small' }}
                        >
                            {
                                this.schemaEntries(
                                    this.parseSchema(
                                        !isEmpty(innerRowSpecificSchemas) && innerRowSpecificSchemas[index]
                                            ? innerRowSpecificSchemas[index] : schema, `${name}.${index}.`,
                                    ),
                                ).map(([key, field]) => (
                                    <Box width="100%" key={key} flex>{field}</Box>
                                ))
                            }
                            <Box width="40px" pad={{ bottom: '4px' }} direction="row" gap="xsmall" justify="center">
                                <Button
                                    onClick={(e) => {
                                        arrayHelpers.remove(index);
                                        if (innerRowSpecificSchemas) {
                                            const newInnerRowSpecificSchemas = cloneDeep(innerRowSpecificSchemas);
                                            delete newInnerRowSpecificSchemas[index];
                                            this.setState({
                                                innerRowSpecificSchemas: newInnerRowSpecificSchemas,
                                            });
                                        }
                                        triggerNativeEvent(e.target, 'change');
                                    }}
                                    alignSelf="end"
                                    icon={<FormSubtract />}
                                    style={{ padding: '6px' }}
                                    disabled={index === 0 && disableFormClose}
                                />

                                {index === values.length - 1 ? (
                                    <Button
                                        onClick={handleAddRow}
                                        icon={<FormAdd />}
                                        alignSelf="end"
                                        style={{ padding: '6px' }}
                                        disabled={disablePlusButton}
                                    />
                                ) : null }
                            </Box>

                        </Box>
                    ))
                ) : (
                    <Button
                        onClick={handleAddRow}
                        label={addItemLabel}
                        disabled={disableFormButton}
                    />
                )}
            </Box>
        );
    };

    renderMultiple() {
        const {
            field: { name }, help_text: help, required, label, theme,
        } = this.props;
        const { formField: { error: errorStyle, label: labelStyle, help: helpStyle } } = theme;

        return (
            <Box flex={false}>
                {label && (
                    <Text as="label" htmlFor={`id-${name}`} {...labelStyle}>
                        {label}
                        {required ? <RequiredAsterisk /> : null}
                    </Text>
                )}
                <FieldArray name={name} render={this.renderRow} />
                {help && (
                    <Text {...helpStyle}>{help}</Text>
                )}
                <ErrorMessage name={name}>
                    {(msg) => (typeof msg === 'string' ? <Text {...errorStyle}>{msg}</Text> : null)}
                </ErrorMessage>
            </Box>
        );
    }

    renderSingle() {
        const {
            field: { name }, help_text: help, required, label, theme, schema,
        } = this.props;
        const { formField: { error: errorStyle, label: labelStyle, help: helpStyle } } = theme;

        return (
            <Box flex={false}>
                {label && (
                    <Text as="label" htmlFor={`id-${name}`} {...labelStyle}>
                        {label}
                        {required ? <RequiredAsterisk /> : null}
                    </Text>
                )}
                <Box pad="small">
                    {
                        this.schemaValues(this.parseSchema(schema, `${name}.`))
                    }
                </Box>
                {help && (
                    <Text {...helpStyle}>{help}</Text>
                )}
                <ErrorMessage name={name}>
                    {(msg) => (typeof msg === 'string' ? <Text {...errorStyle}>{msg}</Text> : null)}
                </ErrorMessage>
            </Box>
        );
    }

    render() {
        const { many } = this.props;

        if (many) {
            return this.renderMultiple();
        }

        return this.renderSingle();
    }
}


export default withTheme(InlineForm);
